/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Sticky Bottom Voice Audition Player Bar (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect } from 'react';
import {
  Play,
  Pause,
  X,
  Volume2,
  Sliders,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useVoiceCatalogStore } from '../../stores/useVoiceCatalogStore';
import { MiniWaveform } from './MiniWaveform';

interface VoiceAuditionPlayerProps {
  onUseVoice?: (voiceId: string) => void;
}

export const VoiceAuditionPlayer: React.FC<VoiceAuditionPlayerProps> = ({ onUseVoice }) => {
  const { t } = useTranslation();
  const {
    isPlaying,
    playbackProgress,
    playbackSpeed,
    getActiveAuditionVoice,
    playAudition,
    pauseAudition,
    stopAudition,
    setPlaybackSpeed,
    setPlaybackProgress,
  } = useVoiceCatalogStore();

  const activeVoice = getActiveAuditionVoice();

  // Simulated audio playback advancement
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (isPlaying && activeVoice) {
      interval = setInterval(() => {
        setPlaybackProgress(
          playbackProgress >= 100 ? 0 : playbackProgress + 2 * playbackSpeed
        );
      }, 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, activeVoice, playbackProgress, playbackSpeed, setPlaybackProgress]);

  if (!activeVoice) return null;

  const currentSeconds = Math.floor((playbackProgress / 100) * 8);
  const totalSeconds = 8;
  const formatTime = (sec: number) => `00:0${sec}`;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-64 md:right-8 z-40 bg-surface/95 backdrop-blur-md border border-primary/30 rounded-2xl p-3 sm:p-4 shadow-2xl animate-slide-up flex flex-col sm:flex-row items-center justify-between gap-3">
      {/* Voice Info */}
      <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
        <div className="size-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0">
          <Volume2 className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-foreground truncate">
              {activeVoice.name}
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.2 rounded-full bg-surface-elevated text-foreground-muted border border-surface-border">
              {activeVoice.languageLabel}
            </span>
          </div>
          <p className="text-[11px] text-foreground-muted truncate">
            {activeVoice.accentRegion} &bull; {activeVoice.styleCategory}
          </p>
        </div>
      </div>

      {/* Playback Controls & Waveform */}
      <div className="flex items-center gap-3 w-full sm:flex-1 max-w-md justify-center">
        {/* Play/Pause */}
        <button
          type="button"
          onClick={() => (isPlaying ? pauseAudition() : playAudition(activeVoice.id))}
          className="size-9 rounded-full bg-primary text-primary-fg hover:bg-primary/90 flex items-center justify-center shadow-sm shrink-0 transition-transform active:scale-95"
        >
          {isPlaying ? (
            <Pause className="size-4" />
          ) : (
            <Play className="size-4 fill-current ml-0.5" />
          )}
        </button>

        {/* Progress Timeline & Waveform */}
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between text-[10px] font-mono text-foreground-muted">
            <span>{formatTime(currentSeconds)}</span>
            <div className="flex-1 mx-3">
              <MiniWaveform isPlaying={isPlaying} progress={playbackProgress} barCount={24} />
            </div>
            <span>{formatTime(totalSeconds)}</span>
          </div>

          <div
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const percent = Math.min(100, Math.max(0, (clickX / rect.width) * 100));
              setPlaybackProgress(percent);
            }}
            className="h-1.5 w-full bg-surface-elevated rounded-full overflow-hidden cursor-pointer relative"
          >
            <div
              className="h-full bg-primary rounded-full transition-all duration-100"
              style={{ width: `${playbackProgress}%` }}
            />
          </div>
        </div>

        {/* Replay */}
        <button
          type="button"
          onClick={() => setPlaybackProgress(0)}
          title={t('voice.replayAudition')}
          className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
        >
          <RotateCcw className="size-3.5" />
        </button>
      </div>

      {/* Speed & Use Voice Action */}
      <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
        {/* Speed Selector */}
        <div className="flex items-center gap-1 bg-surface-elevated px-2 py-1 rounded-lg border border-surface-border text-xs text-foreground-muted">
          <Sliders className="size-3" />
          <select
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
            className="bg-transparent text-xs text-foreground font-mono focus:outline-none cursor-pointer"
          >
            <option value="0.8">0.8x</option>
            <option value="1.0">1.0x</option>
            <option value="1.2">1.2x</option>
            <option value="1.5">1.5x</option>
          </select>
        </div>

        {/* Use Voice Button */}
        <button
          type="button"
          onClick={() => onUseVoice?.(activeVoice.id)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-sm transition-colors whitespace-nowrap"
        >
          <span>{t('voice.useThisVoice')}</span>
          <ArrowRight className="size-3" />
        </button>

        {/* Close Button */}
        <button
          type="button"
          onClick={stopAudition}
          title={t('cat.closeModal')}
          className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};
